import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminHomeContent = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [content, setContent] = useState({
    heroTitle: '',
    heroSubtitle: '',
    heroDescription: '',
    ctaText: '',
    sellingPoint1: '',
    sellingPoint2: '',
    sellingPoint3: '',
    sellingPoint4: ''
  });

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchContent();
  }, [navigate]);

  const fetchContent = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/home-content`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.heroTitle) {
          setContent(data);
        }
      }
    } catch (error) {
      toast.error('Failed to load home content');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setContent({ ...content, [field]: value });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      
      await fetch(`${BACKEND_URL}/api/admin/home-content`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(content)
      });
      
      toast.success('Home page content updated successfully!');
    } catch (error) {
      toast.error('Failed to update content');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="bg-gradient-to-r from-cyan-600 to-emerald-600 text-white p-6 shadow-lg">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Link to="/admin/dashboard">
              <Button variant="outline" size="icon" className="text-white border-white hover:bg-white/10">
                <ArrowLeft className="w-4 h-4" />
              </Button>
            </Link>
            <div>
              <h1 className="text-3xl font-bold">Home Page Content</h1>
              <p className="text-cyan-100">Edit hero text and key selling points</p>
            </div>
          </div>
          <Button onClick={handleSave} disabled={saving} className="bg-white text-cyan-600 hover:bg-cyan-50">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save Changes
          </Button>
        </div>
      </div>

      <div className="container mx-auto p-6 space-y-6">
        {/* Hero Section */}
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">Hero Section</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Hero Title</Label>
              <Input
                value={content.heroTitle}
                onChange={(e) => handleChange('heroTitle', e.target.value)}
                placeholder="Main headline (e.g., Crystal Clear Windows)"
              />
            </div>
            <div>
              <Label>Hero Subtitle</Label>
              <Input
                value={content.heroSubtitle}
                onChange={(e) => handleChange('heroSubtitle', e.target.value)}
                placeholder="Tagline (e.g., Professional Window Cleaning in Neath)"
              />
            </div>
            <div>
              <Label>Hero Description</Label>
              <Textarea
                value={content.heroDescription}
                onChange={(e) => handleChange('heroDescription', e.target.value)}
                placeholder="Brief description about your service"
                rows={3}
              />
            </div>
            <div>
              <Label>Call-to-Action Button Text</Label>
              <Input
                value={content.ctaText}
                onChange={(e) => handleChange('ctaText', e.target.value)}
                placeholder="Button text (e.g., Get a Free Quote)"
              />
            </div>
          </CardContent>
        </Card>

        {/* Key Selling Points */}
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">Key Selling Points</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Selling Point 1</Label>
              <Input
                value={content.sellingPoint1}
                onChange={(e) => handleChange('sellingPoint1', e.target.value)}
                placeholder="e.g., Since 2003"
              />
            </div>
            <div>
              <Label>Selling Point 2</Label>
              <Input
                value={content.sellingPoint2}
                onChange={(e) => handleChange('sellingPoint2', e.target.value)}
                placeholder="e.g., Trusted by same customers 20+ years"
              />
            </div>
            <div>
              <Label>Selling Point 3</Label>
              <Input
                value={content.sellingPoint3}
                onChange={(e) => handleChange('sellingPoint3', e.target.value)}
                placeholder="e.g., <1% callback rate"
              />
            </div>
            <div>
              <Label>Selling Point 4</Label>
              <Input
                value={content.sellingPoint4}
                onChange={(e) => handleChange('sellingPoint4', e.target.value)}
                placeholder="e.g., No upfront payment required"
              />
            </div>
          </CardContent>
        </Card>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> Changes made here will be reflected on the home page hero section. 
            Make sure your content is clear, concise, and compelling to convert visitors into customers.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminHomeContent;
